<template>
  <div class="form-container">
    <h3>Fill up your details and we would be delighted to assist you</h3>
    <form @submit.prevent="submitForm">
      <div class="form-group">
        <label v-if="project" for="project">Package Name:</label>
        <input class="white-text" type="text" id="project" v-model="localProject" name="package-name" placeholder="Package*" required>
      </div>
      <div class="form-group">
        <label v-if="name" for="name">Name:</label>
        <input class="white-text" type="text" id="name" v-model="name" placeholder="Name*" required>
      </div>
      <div class="form-group">
        <label v-if="phone" for="phone">Phone:</label>
        <input class="white-text" type="tel" id="phone" v-model="formattedPhone" @input="formatPhone" placeholder="Phone*" required>
      </div>
      <div class="form-group">
        <label v-if="email" for="email">Email:</label>
        <input class="white-text" type="email" id="email" v-model="email" placeholder="Email*" required>
      </div>
      <div class="form-group">
        <label v-if="message" for="message">Additional Info:</label>
        <textarea class="white-text" id="message" v-model="message" placeholder="Additional Info*" required></textarea>
      </div>
      <label for="appointmentDate">Installation Schedule:</label>
      <div class="form-group">
        <div class="form-row">
          <input class="white-text" type="date" id="appointmentDate" v-model="appointmentDate" placeholder="Appointment Date*" required>
          <input class="white-text" type="time" id="appointmentTime" v-model="appointmentTime" placeholder="Appointment Time*" required>
        </div>
      </div>
      <popUp2 :is-visible="showModal" :modal-message="modalMessage" @close="showModal = false" />
      <button type="submit" class="ff-btn ff-btn-submit white-text" :disabled="formSubmitted">
        {{ formSubmitted ? 'Submitting...' : 'Yes! Contact Me Now' }}
      </button>
    </form>
  </div>
</template>


<script>
import { ref, watch } from 'vue';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { gql } from '@apollo/client/core';
import popUp2 from './popUp2.vue'; // Path to your modal component

export default {
  name: 'tmReg',
  components: {
    popUp2,
  },
  props: {
    project: {
      type: String,
      required: true,
    },
  },
  setup(props, { emit }) {
    const localProject = ref(props.project);
    const formSubmitted = ref(false);
    const showModal = ref(false);
    const modalMessage = ref('');
    const name = ref('');
    const phone = ref('');
    const email = ref('');
    const message = ref('');
    const appointmentDate = ref('');
    const appointmentTime = ref('');

    const formatPhone = (event) => {
      const input = event.target;
      const trimmedValue = input.value.replace(/\D/g, '').substring(0, 10);
      const formattedValue = trimmedValue.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
      input.value = formattedValue;
      phone.value = trimmedValue;
    };

    watch(() => props.project, (newVal) => {
      localProject.value = newVal;
    });

    const close = () => {
      emit('close');
    };

    const CHECK_CONTACT = gql`
      query ($phone: String) {
        contacts(filters: { phone: { eq: $phone } }) {
          data {
            id
            attributes {
              name
              phone
            }
          }
        }
      }
    `;

    const ADD_CONTACT = gql`
      mutation AddContact($name: String!, $project: String!, $email: String!, $phone: String!, $message: String!, $AppointmentDate: Date!, $AppointmentTime: Time!) {
        createContact(data: { project: $project, name: $name, email: $email, phone: $phone, message: $message, AppointmentDate: $AppointmentDate, AppointmentTime: $AppointmentTime }) {
          data {
            id
            attributes {
              project
              name
              email
              phone
              message
              AppointmentDate
              AppointmentTime
            }
          }
        }
      }
    `;

    const UPDATE_CONTACT = gql`
      mutation UpdateContact($id: ID!, $project: String!, $name: String!, $email: String!, $phone: String!, $message: String!, $AppointmentDate: Date!, $AppointmentTime: Time!) {
        updateContact(id: $id, data: { project: $project, name: $name, email: $email, phone: $phone, message: $message, AppointmentDate: $AppointmentDate, AppointmentTime: $AppointmentTime }) {
          data {
            id
            attributes {
              project
              name
              email
              phone
              message
              AppointmentDate
              AppointmentTime
            }
          }
        }
      }
    `;

    const { mutate: addContact } = useMutation(ADD_CONTACT);
    const { mutate: updateContact } = useMutation(UPDATE_CONTACT);

    const { refetch: refetchContact, loading: checkContactLoading, error: checkContactError } = useQuery(CHECK_CONTACT, {
      variables: { phone: phone.value },
      skip: true,
    });

    const submitForm = async () => {
      formSubmitted.value = true;
      try {
        const { data } = await refetchContact({ phone: phone.value });
        
        if (checkContactError.value) {
          console.error('Error fetching contact:', checkContactError.value);
          return;
        }

        if (checkContactLoading.value) {
          console.log('Waiting for contact check...');
          return;
        }

        if (!data.contacts.data || data.contacts.data.length === 0) {
          console.log('Contact does not exist. Creating new contact.');
          const response = await addContact({
            project: localProject.value,
            name: name.value,
            phone: phone.value,
            email: email.value,
            message: message.value,
            AppointmentDate: appointmentDate.value,
            AppointmentTime: appointmentTime.value + ":00",
          });
          console.log('Create Success:', response);
          formSubmitted.value = false;
          showModal.value = true;
          modalMessage.value = 'Record created.';
          return;
        }

        const contact = data.contacts.data[0];

        console.log('Contact exists. Updating contact with ID:', contact.id);
        const response = await updateContact({
          id: contact.id,
          project: localProject.value,
          name: name.value,
          phone: phone.value,
          email: email.value,
          message: message.value,
          AppointmentDate: appointmentDate.value,
          AppointmentTime: appointmentTime.value + ":00",
        });
        console.log('Update Success:', response);
        formSubmitted.value = false;
        showModal.value = true;
        modalMessage.value = 'Record updated.';
      } catch (error) {
        formSubmitted.value = false;
        console.error('Error:', error);
      }
    };

    return {
      localProject,
      name,
      phone,
      email,
      message,
      appointmentDate,
      appointmentTime,
      formSubmitted,
      submitForm,
      formatPhone,
      showModal,
      modalMessage,
      close,
    };
  },
};
</script>

<style scoped>
.form-row {
  display: flex;
  justify-content: space-between;
}
.form-container {
  background-image: url('https://res.cloudinary.com/dcqqec7wa/image/upload/v1716336696/Commons/pexels-catiamatos-1072179_y9sydq.jpg');
  background-size: cover;
  background-position: center;
  padding: 20px;
  border-radius: 8px;
}

h1 {
  text-align: center;
  color: #faf8f8;
}

h3 {
  text-align: center;
  color: #ffffff;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #eeeaea;
}

input[type="text"],
input[type="tel"],
input[type="email"],
input[type="project"],
input[type="date"],
input[type="time"],
textarea {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-sizing: border-box;
}

textarea {
  resize: vertical;
}

.submit-button {
  width: 100%;
  padding: 10px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: white;
  font-size: 16px;
  cursor: pointer;
}

.white-text {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  box-sizing: border-box;
  color: white; /* Set text color to white */
}

.submit-button:hover {
  background-color: #0056b3;
}
</style>
